import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Dog, MapPin, ShieldCheck, Users } from "lucide-react";
import { Container, Row, Col, Modal, ModalBody } from "reactstrap";

import MainNavbar from "components/Navbars/MainNavbar.js";
import LandingPageHeader from "components/Headers/LandingPageHeader.js";
import PuppyCarousel from "components/Other/PuppyCarousel.js";
import DefaultFooter from "components/Footers/DefaultFooter.js";
import { PedigreeModal } from "components/Dogs/DogComponents.js";

export const carouselItems = images => images.map(src => ({ src }));

export function useLandingPageClasses() {
  React.useEffect(() => {
    document.body.classList.add("landing-page");
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");

    return function cleanup() {
      document.body.classList.remove("landing-page");
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);
}

export function LitterPage({
  headerBg,
  title,
  subtitle,
  children,
  pageClassName = "",
  footerVariant
}) {
  useLandingPageClasses();

  const content = (
    <>
      <MainNavbar />
      <div className="wrapper">
        <LandingPageHeader bg={headerBg} title={title} subtitle={subtitle} />
        {children}
        <DefaultFooter variant={footerVariant} />
      </div>
    </>
  );

  return pageClassName ? <div className={`${pageClassName} site-bg-light-grey`}>{content}</div> : content;
}

export function ClickableImageModal({ image, alt = "", className = "img-fluid img-raised" }) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <button
        className="p-0 border-0 bg-transparent"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        <img alt={alt} className={className} src={image} />
      </button>
      <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} style={{ maxWidth: "900px" }}>
        <div className="modal-header justify-content-center">
          <button className="close" type="button" onClick={() => setIsOpen(false)}>
            <i className="now-ui-icons ui-1_simple-remove"></i>
          </button>
        </div>
        <ModalBody>
          <img alt={alt} src={image} />
        </ModalBody>
      </Modal>
    </>
  );
}

function getLitterFactIcon(label) {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes("born") || normalizedLabel.includes("date")) return Calendar;
  if (normalizedLabel.includes("pupp")) return Dog;
  if (normalizedLabel.includes("parent")) return Users;
  if (normalizedLabel.includes("location")) return MapPin;

  return null;
}

export function LitterFactsBar({ facts }) {
  return (
    <div className="litter-facts-bar">
      {facts.map(fact => {
        const Icon = getLitterFactIcon(fact.label);

        return (
          <div className={`litter-fact${Icon ? " litter-fact-with-icon" : ""}`} key={fact.label}>
            {Icon && (
              <span className="litter-fact-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={2.2} />
              </span>
            )}
            <span className="litter-fact-label">{fact.label}</span>
            <strong>{fact.value}</strong>
          </div>
        );
      })}
    </div>
  );
}

export function LitterParentHealthSummary({ parents }) {
  return (
    <section className="litter-health-panel" aria-labelledby="litter-health-heading">
      <div className="litter-health-heading">
        <ShieldCheck aria-hidden="true" size={18} strokeWidth={2.2} />
        <h3 id="litter-health-heading">Health &amp; Clearances</h3>
      </div>
      <span className="litter-health-heading-line" aria-hidden="true"></span>
      <div className="litter-health-columns">
        {parents.map(parent => (
          <article className="litter-health-parent" key={parent.name}>
            <header>
              <h4>{parent.name}</h4>
              {parent.breed && <p>{parent.breed}</p>}
            </header>
            <ul>
              {parent.results.map(result => (
                <li key={`${parent.name}-${result.label}`}>
                  <span className="litter-health-check" aria-hidden="true">✓</span>
                  <span>
                    <strong>{result.label}:</strong> {result.value}
                  </span>
                </li>
              ))}
            </ul>
            {parent.ofaUrl && (
              <a className="litter-health-link" href={parent.ofaUrl} target="_blank" rel="noreferrer">
                OFA results <ArrowRight aria-hidden="true" size={15} strokeWidth={2.3} />
              </a>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

export function LitterIntro({
  title,
  eyebrow = "Sport Mix Litter - 2022",
  announcement,
  announcementAlt,
  children,
  finalCopy,
  healthParents,
  overviewCopy,
  variant = ""
}) {
  if (variant === "modern") {
    return (
      <>
        <div className="litter-back-link">
          <Link to="/litters">
            <i className="now-ui-icons arrows-1_minimal-left"></i> Back to Litters
          </Link>
        </div>
        <div className="litter-intro-modern">
          <div className="litter-intro-left">
            <div className="litter-intro-image">
              <ClickableImageModal
                image={announcement}
                alt={announcementAlt || title}
                className="img-fluid"
              />
            </div>
            {healthParents && <LitterParentHealthSummary parents={healthParents} />}
          </div>
          <div className="litter-intro-right">
            <div className="litter-intro-heading">
              <p className="litter-detail-eyebrow">{eyebrow}</p>
              <h2>{title}</h2>
              <span className="dog-detail-heading-line" aria-hidden="true"></span>
            </div>
            <div className="litter-intro-main-copy">
              {overviewCopy || children}
            </div>
            {finalCopy && <div className="litter-intro-final-copy">{finalCopy}</div>}
          </div>
        </div>
      </>
    );
  }

  return (
    <Row>
      <Col md="5">
        <ClickableImageModal image={announcement} alt={announcementAlt || title} />
      </Col>
      <Col md="7">
        <h2 className="title pt-0">{title}</h2>
        {children}
      </Col>
    </Row>
  );
}

export function ParentPedigree({ parents, pedigree, className = "pt-5", variant = "" }) {
  const [isPedigreeOpen, setIsPedigreeOpen] = React.useState(false);
  const pedigreeTitle = parents.map(parent => parent.name).join(" × ");
  const linkedDogs = parents
    .filter(parent => parent.href)
    .map(parent => ({
      id: parent.href,
      name: parent.name,
      relationship: parent.relationship || "Parent",
      breed: parent.breed,
      image: parent.image,
      profileHref: parent.href,
      litter: parent.litter
    }))
    .concat(pedigree.linkedDogs || []);
  const pedigreeModalData = {
    image: pedigree.image,
    alt: pedigree.alt || `${pedigreeTitle} pedigree`,
    linkedDogs
  };

  if (variant === "modern") {
    return (
      <div className={`litter-parent-pedigree ${className}`}>
        <div className="litter-parent-list">
          {parents.map(parent => {
            const content = (
              <>
                <img
                  alt={parent.name}
                  className={parent.imageClassName || "img-fluid"}
                  src={parent.image}
                />
                <span className={`litter-parent-copy${parent.href ? " litter-parent-copy-with-arrow" : ""}`}>
                  <span>
                    <span className="litter-parent-name">{parent.name}</span>
                    {parent.breed && <span className="litter-parent-breed">{parent.breed}</span>}
                  </span>
                  {parent.href && (
                    <ArrowRight
                      aria-hidden="true"
                      className="litter-parent-arrow"
                      size={22}
                      strokeWidth={2.3}
                    />
                  )}
                </span>
              </>
            );

            return parent.href ? (
              <a href={parent.href} className="litter-parent-card" key={parent.name}>
                {content}
              </a>
            ) : (
              <span className="litter-parent-card" key={parent.name}>
                {content}
              </span>
            );
          })}
        </div>
        <div className="litter-pedigree-panel">
          <img alt={pedigree.alt || "Pedigree"} className="img-fluid" src={pedigree.image} />
          {pedigree.image && (
            <button className="litter-text-link" type="button" onClick={() => setIsPedigreeOpen(true)}>
              View pedigree <ArrowRight aria-hidden="true" size={17} strokeWidth={2.3} />
            </button>
          )}
          <PedigreeModal
            dogName={pedigreeTitle || "This litter"}
            isOpen={isPedigreeOpen}
            pedigree={pedigreeModalData}
            toggle={() => setIsPedigreeOpen(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <Row className={className}>
      <Col md="3">
        {parents.map(parent => {
          const content = (
            <>
              <img
                alt={parent.name}
                className={parent.imageClassName || "img-fluid img-raised"}
                src={parent.image}
              />
              <div className="dogs-name">
                <span>{parent.name}</span>
              </div>
            </>
          );

          return parent.href ? (
            <a href={parent.href} className="dogs-link" key={parent.name}>
              {content}
            </a>
          ) : (
            <span className="dogs-link" key={parent.name}>
              {content}
            </span>
          );
        })}
      </Col>
      <Col md="9" className="align-middle">
        <img alt={pedigree.alt || "Pedigree"} className="img-fluid pt-5" src={pedigree.image} />
        {pedigree.image && (
          <button className="litter-text-link" type="button" onClick={() => setIsPedigreeOpen(true)}>
            View pedigree <ArrowRight aria-hidden="true" size={17} strokeWidth={2.3} />
          </button>
        )}
        <PedigreeModal
          dogName={pedigreeTitle || "This litter"}
          isOpen={isPedigreeOpen}
          pedigree={pedigreeModalData}
          toggle={() => setIsPedigreeOpen(false)}
        />
      </Col>
    </Row>
  );
}

export function ImagePairSection({ title, images }) {
  return (
    <>
      <h2 className="title text-center pt-5">{title}</h2>
      <div className="team pb-5">
        <Row>
          {images.map(image => (
            <Col md="6" key={image.alt || image.src}>
              <img alt={image.alt || ""} className="img-fluid" src={image.src} />
            </Col>
          ))}
        </Row>
      </div>
    </>
  );
}

export function VideoSection({ title, subtitle, videos, className = "team", variant = "" }) {
  const videoRows = [];
  for (let index = 0; index < videos.length; index += 2) {
    videoRows.push(videos.slice(index, index + 2));
  }

  if (variant === "modern") {
    return (
      <section className={`litter-video-section ${className}`}>
        {title && (
          <div className="litter-detail-section-heading">
            <h2>{title}</h2>
            <span className="dog-detail-heading-line" aria-hidden="true"></span>
          </div>
        )}
        {subtitle && <p className="litter-video-subtitle">{subtitle}</p>}
        <div className="litter-video-grid">
          {videos.map(video => (
            <article className="litter-video-card" key={video.src}>
              <div className="video-frame">
                <iframe
                  src={video.src}
                  title={video.title || video.caption || "Video"}
                  scrolling={video.scrolling || undefined}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                ></iframe>
              </div>
              {(video.caption || video.title) && <p>{video.caption || video.title}</p>}
            </article>
          ))}
        </div>
      </section>
    );
  }

  return (
    <>
      {title && <h2 className="title text-center pt-5">{title}</h2>}
      {subtitle && <h3 className="title text-center">{subtitle}</h3>}
      <div className={className}>
        {videoRows.map(row => (
          <Row className="video-section-row" key={row.map(video => video.src).join("-")}>
            {row.map(video => (
              <Col md="6" className="text-center mb-4" key={video.src}>
                {/*
                  `title` doubles as the iframe's accessible name in older litter data.
                  Show it below the video unless a more specific caption is provided.
                */}
                <div className="video-frame">
                  <iframe
                    src={video.src}
                    title={video.title || video.caption || "Video"}
                    scrolling={video.scrolling || undefined}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                  ></iframe>
                </div>
                {(video.caption || video.title) && <p>{video.caption || video.title}</p>}
              </Col>
            ))}
          </Row>
        ))}
      </div>
    </>
  );
}

export function SectionDividerImage({ image }) {
  return (
    <div className="section section-dog text-center">
      <div
        className="image-fullw section-sep-image"
        style={{ backgroundImage: "url(" + image + ")" }}
      >
        <div className="wrapper"></div>
      </div>
    </div>
  );
}

export function LitterPuppyGrid({ summary, puppies, variant = "" }) {
  if (variant === "modern") {
    return (
      <section className="litter-puppy-section site-bg-light-grey">
        <Container>
          <div className="litter-detail-section-heading litter-detail-section-heading-left">
            <h2>Litter Information</h2>
            <span className="dog-detail-heading-line" aria-hidden="true"></span>
            {summary && <p>{summary}</p>}
          </div>
          <div className="litter-puppy-grid-modern">
            {puppies.map(puppy => (
              <PuppyCard puppy={puppy} variant="modern" key={puppy.name} />
            ))}
          </div>
        </Container>
      </section>
    );
  }

  return (
    <Container>
      <h2 className="title text-center">The Litter</h2>
      <div className="team text-center">
        {summary && (
          <Row>
            <Col md="3"></Col>
            <Col md="6">
              <p>{summary}</p>
            </Col>
          </Row>
        )}
        <Row>
          {puppies.map(puppy => (
            <PuppyCard puppy={puppy} key={puppy.name} />
          ))}
        </Row>
      </div>
    </Container>
  );
}

export function PuppyCard({ puppy, variant = "" }) {
  if (variant === "modern") {
    return (
      <article className="litter-puppy-card">
        <PuppyCarousel items={puppy.images} />
        <div className="litter-puppy-card-copy">
          <h3>{puppy.name}</h3>
          {puppy.registeredName && (
            <p className="registered-name">{puppy.registeredName}</p>
          )}
          <dl>
            {puppy.details.map(detail => (
              <div key={detail.label}>
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </article>
    );
  }

  return (
    <Col md="4">
      <h4 className="title">{puppy.name}</h4>
      {puppy.registeredName && (
        <p className="registered-name text-primary">{puppy.registeredName}</p>
      )}
      <PuppyCarousel items={puppy.images} />
      <div className="mt-3 blockquote text-left">
        {puppy.details.map(detail => (
          <div key={detail.label}>
            <span className="badge badge-neutral">{detail.label}:</span> {detail.value}
          </div>
        ))}
      </div>
    </Col>
  );
}

export function LitterListingGrid({ litters, rowClassName = "", cardClassName = "" }) {
  return (
    <Row className={rowClassName}>
      {litters.map(litter => {
        const isNameplateCard = cardClassName.includes("litter-nameplate-card");
        const isFeatureCard = cardClassName.includes("litter-feature-card");
        const isGalleryCard = cardClassName.includes("litter-gallery-card");
        const titleMatch = litter.title.match(/^(.*?)\s*\((.*?)\)$/);
        const pairingName = titleMatch ? titleMatch[1] : litter.title;
        const displayPairingName = isFeatureCard ? pairingName.replace(/\s+x\s+/i, " × ") : pairingName;
        const year = titleMatch ? titleMatch[2] : "";
        const galleryMetadata = [litter.status, year].filter(Boolean).join(" · ");
        const isCompactCaptionCard = isFeatureCard || isGalleryCard;

        return (
          <Col
            md={isFeatureCard ? 10 : 4}
            className={cardClassName ? "mb-4" : "text-center"}
            key={litter.href}
          >
            {isNameplateCard ? (
              <Link
                aria-label={`View ${litter.title}`}
                className={cardClassName}
                to={litter.href}
              >
                {litter.image && (
                  <span className="litter-nameplate-image">
                    <img
                      alt={litter.title}
                      src={litter.image}
                      style={litter.imagePosition ? { objectPosition: litter.imagePosition } : undefined}
                    />
                  </span>
                )}
                <span className="litter-nameplate">
                  <span className="litter-nameplate-copy">
                    {isCompactCaptionCard ? (
                      galleryMetadata && <span className="litter-nameplate-meta">{galleryMetadata}</span>
                    ) : (
                      litter.status && <span className="litter-nameplate-type">{litter.status}</span>
                    )}
                    <span className="litter-nameplate-title">{displayPairingName}</span>
                    {!isCompactCaptionCard && year && <span className="litter-nameplate-year">{year}</span>}
                  </span>
                  <ArrowRight aria-hidden="true" size={26} strokeWidth={2.25} />
                </span>
              </Link>
            ) : (
              <a href={litter.href}>
                <h4>{litter.title}</h4>
                {litter.image && (
                  <img alt={litter.title} className="img-raised" src={litter.image} />
                )}
              </a>
            )}
          </Col>
        );
      })}
    </Row>
  );
}
